import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Alert, Banner, CloseButton, TextButton } from '@zillow/constellation-10';

import { parseQueryString } from '@zg-rentals/string-utils';
import {
  EmailVerificationType,
  useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken,
  useRentalManagerUserEmailVerificationWebApiHandler_verifySecurityToken,
} from '@zg-rentals/rental-manager-env';

import { EmailVerificationSelectors, RoutingSelectors, UserSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import EmailVerificationActions from '../../../actions/EmailVerificationActions';
import accessUtils from '../../../utils/accessUtils';

import { updateEmailVerified } from '../../../slices/user';

import QueryConstants from '../../../constants/QueryConstants';

import VerifyEmailModal from '../../../components/Modal/VerifyEmailModal';

import { BannerBox } from '../styles';

const EmailVerification = (): JSX.Element => {
  const dispatch = useDispatch();

  const {
    restrictions: { isAllowed: isUserAllowed },
    isPostingPathAdmin,
    isEmailVerified,
    isEnterpriseCustomer,
  } = useSelector((state) => UserSelectors.makeUser(state));
  const { hasFeedListings, hasPendingEmailVerification, accountEmailVerified } = useSelector((state) =>
    EmailVerificationSelectors.makeEmailVerification(state),
  );
  const { cookieEnabledServices, canAccessZramMultifamilyPropertyTable } = useSelector(
    (state) => ({
      cookieEnabledServices: state.app.cookieEnabledServices,
      canAccessZramMultifamilyPropertyTable: state.user.canAccessZramMultifamilyPropertyTable,
    }),
    shallowEqual,
  );

  const location = useSelector((state) => RoutingSelectors.makeLocation(state));
  const queryParams = parseQueryString(location.search);
  const securityToken = queryParams[QueryConstants.SECURITY_TOKEN];
  const email = useSelector((state) => UserSelectors.makeEmail(state));

  const [displayEmailVerificationNotification, setDisplayEmailVerificationNotification] = useState(false);
  const [displayPendingEmailVerificationBox, setDisplayPendingEmailVerificationBox] = useState(false);
  const [emailVerificationNotificationText, setEmailVerificationNotificationText] = useState('');
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [emailVerificationFailed, setEmailVerificationFailed] = useState(false);

  const { mutateAsync: sendSecurityToken } = useRentalManagerUserEmailVerificationWebApiHandler_sendSecurityToken();
  const { mutateAsync: verifySecurityToken } = useRentalManagerUserEmailVerificationWebApiHandler_verifySecurityToken();

  const canAccessListingQuality = accessUtils.canAccessListingQuality({
    cookieEnabledServices,
    canAccessZramMultifamilyPropertyTable,
  });

  const handleResendEmailClick = (text: string) => {
    setEmailVerificationNotificationText(text);
    setDisplayEmailVerificationNotification(true);
    setDisplayPendingEmailVerificationBox(false);
  };

  useEffect(() => {
    if (isUserAllowed && securityToken) {
      verifySecurityToken({ getParams: { securityToken } })
        .then((apiResponse) => {
          let notificationText = 'Your email has been verified!';

          if (apiResponse && apiResponse.errorCode && apiResponse.errorCode === 'email-already-verified') {
            notificationText = apiResponse.message as string;
          }

          setDisplayEmailVerificationNotification(true);
          setDisplayPendingEmailVerificationBox(false);
          setEmailVerificationNotificationText(notificationText);
        })
        .catch(() => {
          setShowEmailVerificationModal(true);
          setEmailVerificationFailed(true);
        });
    }

    if (hasFeedListings && !accountEmailVerified && !hasPendingEmailVerification) {
      if (isPostingPathAdmin) {
        setDisplayPendingEmailVerificationBox(true);
      } else {
        sendSecurityToken({
          getParams: { emailVerificationType: EmailVerificationType.ACCOUNT_EMAIL_CLAIM_LISTINGS },
        }).then(() => {
          dispatch(
            EmailVerificationActions.updateEmailVerificationInfo({
              hasPendingEmailVerification: true,
            }),
          );
          setDisplayPendingEmailVerificationBox(true);
        });
      }
    }

    /**
     * NOTE: `user.isEmailVerified` should and will be also true if `accountEmailVerified` is true
     * once `user/get` endpoint is called later. Directly updating `store.user.isEmailVerified` to
     * prevent the need to call `user/get`
     */
    if (accountEmailVerified && !isEmailVerified) {
      dispatch(updateEmailVerified(true));
    }
  }, []);

  return (
    <div>
      {displayEmailVerificationNotification && (
        <BannerBox isEnterpriseCustomer={isEnterpriseCustomer} canAccessListingQuality={canAccessListingQuality}>
          <Banner
            tone="success"
            closeButton={<CloseButton onImpact={true} />}
            onClose={() => setDisplayEmailVerificationNotification(false)}
          >
            {emailVerificationNotificationText}
          </Banner>
        </BannerBox>
      )}
      {displayPendingEmailVerificationBox && (
        <BannerBox isEnterpriseCustomer={isEnterpriseCustomer} canAccessListingQuality={canAccessListingQuality}>
          <Alert
            tone="warning"
            actionButton={<TextButton onClick={() => setShowEmailVerificationModal(true)}>View details</TextButton>}
          >{`The listings associated with your account will be added once you verify your email at ${email}.`}</Alert>
        </BannerBox>
      )}
      {showEmailVerificationModal && (
        <VerifyEmailModal
          onClose={() => setShowEmailVerificationModal(false)}
          handleResendEmailClick={handleResendEmailClick}
          failedVerification={emailVerificationFailed}
        />
      )}
    </div>
  );
};

export default EmailVerification;
